import { Alert, Button, Radio as ChakraRadio, RadioGroup as ChakraRadioGroup, Checkbox, CheckboxGroup, FormControl, FormHelperText, Link, Stack, Text, useDisclosure } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { Error } from 'BrokerTsLib/src/dtos'
import * as global from 'brokertslib/src/helpers/global'
import type { FieldError } from 'BrokerTsLib/src/rhf'
import { Controller, FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { useContext, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'


import ErrorSummary from '../components/ErrorSummary'
import { FormLabel } from '../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionHeader,
  FormSectionTitle,
} from '../components/FormSection'
import { InputGroup } from '../components/InputGroup'
import { MainContent } from '../components/MainContent'
import { RadioStack } from '../components/RadioStack'
import { TextareaGroup } from '../components/TextareaGroup'
import { useAuth } from '../context/AuthContext'
import type { OkAppsMeetingRecord } from '../dtos/OkAppsMeetingRecord'
import { baseUrl } from '../hooks'
import { useGetAllDocuments } from '../hooks/useDocuments'
import { useCreateContract, useSaveMeetingRecord } from '../hooks/useWizardAddon'
import { Check, Duplicate } from '../icons'
import { insuranceParamsContext } from '../layouts/Wizard'
import { meetingRecordContext } from '../pages/carkulka/wizard/5'
import { FormButtons } from './FormButtons'

const insuranceParamsApiUrl = import.meta.env.VITE_INSURANCEPARAMS_VEHICLE_API
const insuranceParamsUrl = `${baseUrl}/${insuranceParamsApiUrl}`

interface MeetingRecordCarExtraFormProps {
  handleCreateContractsCallback: Function
}

export const MeetingRecordCarExtraForm = ({ handleCreateContractsCallback }: MeetingRecordCarExtraFormProps) => {
  const { user } = useAuth() ?? {}
  const { insuranceParamsId, insuranceParams } = useContext(insuranceParamsContext)
  const [acceptChangedPrice, setAcceptChangedPrice] = useState(false)
  const [priceChanged, setPriceChanged] = useState(false)
  const [alerts, setAlerts] = useState<Error[]>([])
  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const { refetchInsuranceParams } = useContext(insuranceParamsContext)
  const { isLoading: isLoadingMR, refetch: refetchMR, meetingRecord } = useContext(meetingRecordContext)
  const { refetch: refetchDocuments } = useGetAllDocuments(insuranceParams!.generalParams.paramsId!, insuranceParams!.generalParams.selectedOfferId!, { enabled: false })

  // save formulare
  const { mutateAsync: saveMeetingRecord, isLoading: isSavingMeetingRecord } = useSaveMeetingRecord()

  // sjednani smlouvy
  const { mutateAsync: createContract, isLoading: isCreatingContract } = useCreateContract()

  // reset formulare po nacteni dat
  useEffect(() => {
    if (meetingRecord)
      reset(meetingRecord)
  }, [!meetingRecord])

  const methods = useForm<OkAppsMeetingRecord>({ defaultValues: meetingRecord })
  const { control, handleSubmit, register, getValues, setValue, reset, setError, formState: { errors, isSubmitting }, getFieldState } = methods

  const isValid = !Object.keys(errors).length

  const allowCreateContract = (user?.insuringAllowed ?? 0) == 1

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  // zobrazeni validace nabidky ve validation summary (zobrazuje se uz pri getu)
  useEffect(() => {
    if (meetingRecord?.errors)
      meetingRecord.errors.forEach(x => setError('' as any, { message: x.message }))
  }, [meetingRecord?.errors])

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const onSubmit = (values: OkAppsMeetingRecord) => {
    if (!values.hasMoreContracts)
      values.hasMoreContracts = false // pokud se nezmění výchozí hodnota (undefined), je třeba nastavit na false
    if (!values.refusesMoreDetails)
      values.refusesMoreDetails = false // pokud se nezmění výchozí hodnota (undefined), je třeba nastavit na false

    return new Promise((resolve, reject) =>
      saveMeetingRecord(values)
        .then((resp) => {
          // v pripade pouze ulozeni jenom refreshneme formular
          if (values.nextPage == 'current') {
            savedFormAlertOnOpen()
            refetchMR()
            window.scrollTo(0, 0)

            // pokud nesjednavam pojistku, tak nepokracuji
            resolve(resp)
            return
          }

          // sjednani pojistky
          createContract({ paramsId: insuranceParamsId!, acceptChangedPrice: acceptChangedPrice })
            .then(resp => {
              resolve(resp)
              if (resp.data.priceChanged)
                setPriceChanged(true)
              else if (resp.data.errors.length > 0)
                setAlerts(resp.data.errors)
              else {
                refetchInsuranceParams()
                refetchMR()
                handleCreateContractsCallback()

                window.scrollTo(0, 0)

                refetchDocuments()
              }
            })
            .catch((err: AxiosError) => {
              reject(err)
            })
        })
        .catch((err) => reject(err))
    )
  }

  const submitForm = handleSubmit((values) => {
    return onSubmit(values)
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data)) return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
  })

  const consequenceTexts: { [key: string]: string } = {
    text1: 'Práva a povinnosti vyplývající z nově sjednané/měněné pojistné smlouvy',
    text2: 'Výluky z pojištění uvedené v pojistných podmínkách',
    text3: 'Vznik smlouvy je podmíněn zaplacením pojistného, které musí být připsáno na účtu pojišťovny dle platebních instrukcí v návrhu smlouvy.',
    text4: 'V důsledku neplacení může dojít ke stornu pojistné smlouvy a tím ke ztrátě pojistné ochrany a krytí rizik.',
    text5: 'Povinnost zajištění prohlídky vozidla/dodání fotodokumentace vozidla'
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Záznam z jednání</FormSectionTitle>
            </FormSectionHeader>

            <FormSectionBody sx={{ gap: 8 }}>
              <FormControl variant="fullWidth">
                <FormLabel optional>
                  Případné nesrovnalosti mezi požadavky Zákazníka a sjednávaným pojištěním nebo jeho
                  podstatnou změnou
                </FormLabel>

                <TextareaGroup
                  placeholder="Uveďte nesrovnalosti mezi nabízeným pojištěním a požadavky zákazníka. Tyto údaje se propíšou do Záznamu z jednání, které zákazník podepisuje."
                  errors={errors as FieldError}
                  {...register('irregularities')}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel tooltip="V případě doporučení sjednání nové pojistné smlouvy nebo její podstatné změny je třeba vysvětlit dopady takového jednání vč. souvisejících rizik u každého sjednaného pojistného produktu.">Popis dopadů sjednání/změny/ukončení pojištění, včetně souvisejících rizik</FormLabel>
                <Stack>
                  <ChakraRadioGroup onChange={(val) => { setValue('consequences', consequenceTexts[val]) }}>
                    <RadioStack variant='simple' sx={{ gridAutoFlow: 'row' }}>
                      <ChakraRadio value="text1">{consequenceTexts['text1']}</ChakraRadio>
                      <ChakraRadio value="text2">{consequenceTexts['text2']}</ChakraRadio>
                      <ChakraRadio value="text3">{consequenceTexts['text3']}</ChakraRadio>
                      <ChakraRadio value="text4">{consequenceTexts['text4']}</ChakraRadio>
                      <ChakraRadio value="text5">{consequenceTexts['text5']}</ChakraRadio>
                    </RadioStack>
                  </ChakraRadioGroup>
                </Stack>
              </FormControl>

              <FormControl variant="fullWidth">
                <TextareaGroup
                  placeholder="Napište popis dopadů..."
                  {...register('consequences')}
                />

                <FormHelperText>
                  V případě doporučení sjednání nové pojistné smlouvy nebo její podstatné změny je třeba vysvětlit dopady takového jednání vč. souvisejících rizik u každého sjednaného pojistného produktu.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel tooltip="Zákazník odmítá sdělit další požadavky?">
                  Zákazník odmítá sdělit další požadavky?
                </FormLabel>

                <Controller
                  name="refusesMoreDetails"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <ChakraRadioGroup onChange={e => onChange(e == '1')} value={value ? '1' : '0'}>
                      <RadioStack variant="stackedTile">
                        <ChakraRadio value={'1'}>Ano</ChakraRadio>
                        <ChakraRadio value={'0'}>Ne</ChakraRadio>
                      </RadioStack>
                    </ChakraRadioGroup>
                  )}
                />
              </FormControl>

              <FormControl>
                <FormLabel tooltip="Má zákazník vedle této pojistné smlouvy uzavřenou další se stejným krytím?">
                  Má zákazník vedle této pojistné smlouvy uzavřenou další se stejným krytím?
                </FormLabel>

                <Controller
                  name="hasMoreContracts"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <ChakraRadioGroup onChange={e => onChange(e == '1')} value={value ? '1' : '0'}>
                      <RadioStack variant="stackedTile">
                        <ChakraRadio value={'1'}>Ano</ChakraRadio>
                        <ChakraRadio value={'0'}>Ne</ChakraRadio>
                      </RadioStack>
                    </ChakraRadioGroup>
                  )}
                />
              </FormControl>

              {/* <FormControl>
                <FormLabel tooltip="Jaké má zákazník znalosti produktů?">
                  Jaké má zákazník znalosti produktů?
                </FormLabel>

                <RadioGroup control={control} name="customerKnowledge">
                  <Stack>
                    <Radio value="1">Zákazník NEMÁ dostatečné znalosti a zkušenosti</Radio>
                    <Radio value="2">
                      Zákazník již měl sjednáno pojištění s obdobným rozsahem pojistného krytí
                    </Radio>
                    <Radio value="3">Zákazník MÁ znalosti z oboru pojišťovnictví</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl> */}

              <Controller
                name="noCollison"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CheckboxGroup onChange={e => onChange(e[0] == '1')} value={value ? ['1'] : undefined}>
                    <Checkbox value={'1'}>
                      Zákazník ani Zprostředkovatel si nejsou vědomi žádných nesrovnalostí mezi požadavky Zákazníka a nabízeným pojištěním.
                    </Checkbox>
                  </CheckboxGroup>
                )} />

              <FormControl isInvalid={!!errors.placeOfContract?.message} isRequired>
                <FormLabel>Místo sjednání</FormLabel>
                <InputGroup placeholder="Např. Brno" {...register('placeOfContract')} errors={errors.placeOfContract} />
              </FormControl>
            </FormSectionBody>
          </FormSection>

          {(priceChanged || alerts.length > 0) &&
            <Alert colorScheme="red" mb={8}>
              <Stack direction='column'>
                {priceChanged && <Text>
                  Před sjednáním došlo ke změně ceny. Vraťte se na
                  <Link as={RouterLink} colorScheme="blue" to={`/carkulka/extra/wizard/2/${insuranceParamsId!}`}>&nbsp;seznam kalkulací&nbsp;</Link>
                  a vybranou nabídku překalkulujte.
                </Text>}

                {alerts.length > 0 && alerts.map(alert => (
                  <Text key={alert.message}>{alert.message}</Text>
                ))}
              </Stack>
            </Alert>
          }

          <ErrorSummary errors={errors} />

          <FormButtons
            leftButtons={
              <Button variant="outline" as='a' href={`${insuranceParamsUrl}/CloneUrl?paramsId=${insuranceParamsId}&replacement=${false}&returnUrl=${encodeURIComponent(`${baseUrl}/carkulka/extra/wizard/1`)}`}>
                <Duplicate color="secondary.500" />
                Klonovat
              </Button>
            }
            rightButtons={
              <>
                <Button
                  variant="outline"
                  disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !isValid}
                  isLoading={isSubmittingCurrent && isValid}
                  type="button"
                  onClick={() => {
                    setValue('nextPage', 'current')
                    setIsSubmittingCurrent(true)
                    submitForm()
                  }}>
                  Uložit
                </Button>

                {allowCreateContract &&
                  <Button
                    colorScheme="green"
                    disabled={isSubmitting || isSubmittingNext || isSubmittingCurrent || !isValid}
                    isLoading={isSubmittingNext && isValid}
                    loadingText="Sjednávám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}
                  >
                    <Check />
                    Sjednat pojištění
                  </Button>
                }


                <SubmitDialog
                  isOpen={isSavedFormAlertOpen}
                  onClose={savedFormAlertOnClose}
                  header='Uloženo'
                  body={'Údaje byly úspěšně uloženy'}
                />
              </>
            }
          />
        </MainContent>
      </form>
    </FormProvider>
  )
}
